// src/components/SalesManagement.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SalesManagement.css";

const API_BASE = "https://dairy.server.statify.co.ke";

function FloatingCard({ title, children, redirectTo, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="floating-card-overlay">
      <div className="floating-card">
        <div className="floating-card-header">
          <h3>{title}</h3>
          <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="floating-card-body">{children}</div>
        <div className="floating-card-footer">
          {redirectTo && (
            <button className="edit-btn" onClick={() => navigate(redirectTo)}>
              Edit Full Form
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SalesManagement() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [cart, setCart] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // floating card states
  const [showCustomerCard, setShowCustomerCard] = useState(false);
  const [showProductCard, setShowProductCard] = useState(false);
  const [showInvoiceCard, setShowInvoiceCard] = useState(false);

  // floating card internals
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "" });
  const [newProduct, setNewProduct] = useState({ name: "", standard_rate: "", cost: "" });
  const [savingCard, setSavingCard] = useState(false);

  // Load customers, products, and sales
  async function loadLists() {
    try {
      const [cRes, pRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/api/customers/`),
        fetch(`${API_BASE}/api/products/`),
        fetch(`${API_BASE}/api/sales/`),
      ]);

      if (!cRes.ok) throw new Error("Failed to load customers");
      if (!pRes.ok) throw new Error("Failed to load products");
      if (!sRes.ok) throw new Error("Failed to load sales");

      const [cData, pData, sData] = await Promise.all([cRes.json(), pRes.json(), sRes.json()]);
      setCustomers(cData);
      setProducts(pData);
      setSales(sData);
    } catch (err) {
      console.error(err);
      alert("Failed to load initial data: " + (err.message || err));
    }
  }

  useEffect(() => {
    loadLists();
  }, []);

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: Number(item.quantity) + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        name: product.name,
        standard_rate: Number(product.standard_rate) || 0,
        quantity: 1
      }]);
    }
  };

  const updateQuantity = (product_id, qty) => {
    const value = qty === "" ? "" : Number(qty);
    setCart(cart.map(item =>
      item.product_id === product_id ? { ...item, quantity: isNaN(value) ? 0 : value } : item
    ));
  };

  const removeFromCart = (product_id) => {
    setCart(cart.filter(item => item.product_id !== product_id));
  };

  const totalAmount = cart.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.standard_rate) || 0),
    0
  );

  // Helper: get customer object by id
  const getCustomerById = (id) => customers.find(c => Number(c.id) === Number(id));

  // Save sale — NOTE: includes buyer_name to satisfy backend requirement
  const saveSale = async () => {
    if (!selectedCustomer) {
      alert("Please select a customer.");
      return;
    }
    if (cart.length === 0) {
      alert("Please add products to the cart.");
      return;
    }

    setLoading(true);
    try {
      const customer = getCustomerById(selectedCustomer);
      const buyer_name = customer ? (customer.name || customer.buyer_name || "") : "";

      const payload = {
        customer_id: Number(selectedCustomer),
        buyer_name: buyer_name,               // <-- required by your backend
        invoice_number: invoiceNumber || null,
        total_amount: Number(totalAmount) || 0,
        sale_items: cart.map(item => ({
          product_id: Number(item.product_id),
          quantity: Number(item.quantity),
          price_per_unit: Number(item.standard_rate),
          amount: Number(item.quantity) * Number(item.standard_rate)
        }))
      };

      const res = await fetch(`${API_BASE}/api/sales/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let msg = text || res.statusText;
        try {
          const json = JSON.parse(text || "{}");
          msg = json.error || json.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const created = await res.json();
      alert("Sale saved successfully!");
      setCart([]);
      setInvoiceNumber("");
      // reload sales list
      const sRes = await fetch(`${API_BASE}/api/sales/`);
      if (sRes.ok) {
        const sData = await sRes.json();
        setSales(sData);
      }
    } catch (err) {
      console.error(err);
      alert("Error saving sale: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // save new customer from floating card
  const saveNewCustomer = async (e) => {
    e && e.preventDefault();
    if (!newCustomer.name || !newCustomer.name.trim()) return alert("Customer name required");
    setSavingCard(true);
    try {
      const res = await fetch(`${API_BASE}/api/customers/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newCustomer)
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || res.statusText);
      }
      const created = await res.json();
      setCustomers(prev => [created, ...prev]);
      setSelectedCustomer(created.id);
      setNewCustomer({ name: "", email: "", phone: "" });
      setShowCustomerCard(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save customer: " + (err.message || err));
    } finally {
      setSavingCard(false);
    }
  };

  // save new product from floating card
  const saveNewProduct = async (e) => {
    e && e.preventDefault();
    if (!newProduct.name || !newProduct.name.trim()) return alert("Product name required");
    setSavingCard(true);
    try {
      const payload = {
        name: newProduct.name,
        standard_rate: newProduct.standard_rate ? Number(newProduct.standard_rate) : null,
        cost: newProduct.cost ? Number(newProduct.cost) : null,
        is_stock_item: true,
        maintain_stock: true,
        is_active: true
      };
      const res = await fetch(`${API_BASE}/api/products/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || res.statusText);
      }
      const created = await res.json();
      setProducts(prev => [created, ...prev]);
      setShowProductCard(false);
      setNewProduct({ name: "", standard_rate: "", cost: "" });
    } catch (err) {
      console.error(err);
      alert("Failed to save product: " + (err.message || err));
    } finally {
      setSavingCard(false);
    }
  };

  // quick invoice setter
  const saveQuickInvoice = (e) => {
    e && e.preventDefault();
    if (!invoiceNumber || !invoiceNumber.trim()) return alert("Invoice number required");
    setShowInvoiceCard(false);
    alert("Invoice number set: " + invoiceNumber);
  };

  return (
    <div className="sales-container">
      <h2>Sales Management</h2>

      <div className="quick-add-buttons">
        <button onClick={() => setShowCustomerCard(true)}>+ New Customer</button>
        <button onClick={() => setShowProductCard(true)}>+ New Product</button>
        <button onClick={() => setShowInvoiceCard(true)}>+ New Invoice</button>
        <button onClick={loadLists}>Refresh</button>
      </div>

      <div className="sales-form">
        <label>Customer</label>
        <select
          value={selectedCustomer}
          onChange={(e) => setSelectedCustomer(e.target.value)}
        >
          <option value="">-- Select Customer --</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <label>Invoice Number</label>
        <input
          type="text"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
        />

        <h3>Products</h3>
        <div className="product-list">
          {products.length === 0 && <div className="muted">No products</div>}
          {products.map(p => (
            <div key={p.id} className="product-card">
              <p><b>{p.name}</b></p>
              <p>Price: {p.standard_rate != null ? Number(p.standard_rate).toFixed(2) : "0.00"}</p>
              <button onClick={() => addToCart(p)}>Add</button>
            </div>
          ))}
        </div>

        <h3>Cart</h3>
        <table className="cart-table">
          <thead>
            <tr>
              <th>Product</th><th>Price</th><th>Qty</th><th>Subtotal</th><th></th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 && (
              <tr><td colSpan="5" className="muted">Cart is empty</td></tr>
            )}
            {cart.map(item => (
              <tr key={item.product_id}>
                <td>{item.name}</td>
                <td>{Number(item.standard_rate || 0).toFixed(2)}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    value={item.quantity}
                    onChange={(e) => updateQuantity(item.product_id, e.target.value)}
                    style={{ width: 80 }}
                  />
                </td>
                <td>{((Number(item.quantity) || 0) * (Number(item.standard_rate) || 0)).toFixed(2)}</td>
                <td><button onClick={() => removeFromCart(item.product_id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>Total: {totalAmount.toFixed(2)}</h3>

        <button disabled={loading} onClick={saveSale}>
          {loading ? "Saving..." : "Save Sale"}
        </button>
      </div>

      <div className="sales-history">
        <h3>Sales History</h3>
        <table>
          <thead>
            <tr>
              <th>Invoice</th><th>Customer</th><th>Total</th><th>Status</th><th>Date</th>
            </tr>
          </thead>
          <tbody>
            {sales.length === 0 && <tr><td colSpan="5" className="muted">No sales yet</td></tr>}
            {sales.map(s => (
              <tr key={s.id}>
                <td>{s.invoice_number || "-"}</td>
                <td>{s.customer?.name || s.buyer_name || "-"}</td>
                <td>{Number(s.total_amount || 0).toFixed(2)}</td>
                <td>{s.payment_status || "-"}</td>
                <td>{s.date ? new Date(s.date).toLocaleString() : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCustomerCard && (
        <FloatingCard
          title="New Customer"
          redirectTo="/customer-form"
          onClose={() => setShowCustomerCard(false)}
        >
          <form onSubmit={saveNewCustomer} className="floating-form">
            <div className="field">
              <label>Name</label>
              <input value={newCustomer.name} onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} />
            </div>
            <div className="field">
              <label>Email</label>
              <input value={newCustomer.email} onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })} />
            </div>
            <div className="field">
              <label>Phone</label>
              <input value={newCustomer.phone} onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
            </div>
            <div className="form-actions" style={{ display: "flex", gap: "10px" }}>
              <button type="button" onClick={() => setShowCustomerCard(false)}>Cancel</button>
              <button type="submit" disabled={savingCard}>{savingCard ? "Saving..." : "Save Customer"}</button>
            </div>
          </form>
        </FloatingCard>
      )}

      {showProductCard && (
        <FloatingCard
          title="New Product"
          redirectTo="/product-form"
          onClose={() => setShowProductCard(false)}
        >
          <form onSubmit={saveNewProduct} className="floating-form">
            <div className="field">
              <label>Name</label>
              <input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
            </div>
            <div className="field">
              <label>Price</label>
              <input type="number" step="0.01" value={newProduct.standard_rate} onChange={e => setNewProduct({ ...newProduct, standard_rate: e.target.value })} />
            </div>
            <div className="field">
              <label>Cost</label>
              <input type="number" step="0.01" value={newProduct.cost} onChange={e => setNewProduct({ ...newProduct, cost: e.target.value })} />
            </div>
            <div className="form-actions" style={{ display: "flex", gap: "10px" }}>
              <button type="button" onClick={() => setShowProductCard(false)}>Cancel</button>
              <button type="submit" disabled={savingCard}>{savingCard ? "Saving..." : "Save Product"}</button>
            </div>
          </form>
        </FloatingCard>
      )}

      {showInvoiceCard && (
        <FloatingCard
          title="Quick Invoice"
          redirectTo="/invoice-form"
          onClose={() => setShowInvoiceCard(false)}
        >
          <form onSubmit={saveQuickInvoice} className="floating-form">
            <div className="field">
              <label>Invoice Number</label>
              <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
            </div>
            <div className="form-actions" style={{ display: "flex", gap: "10px" }}>
              <button type="button" onClick={() => setShowInvoiceCard(false)}>Cancel</button>
              <button type="submit">Set Invoice</button>
            </div>
          </form>
        </FloatingCard>
      )}
    </div>
  );
}
